import { Box, Flex, Icon, Text, VStack } from "@chakra-ui/react";
import { BookOpen } from "lucide-react";
import { FiX } from "react-icons/fi";
import { Button } from "components/ds";

/**
 * InstructionsCallout — admin-authored, per-application reviewer guidance
 * (PLO-4817). Rendered near the review header when the campaign carries an
 * instruction for this review's resource. Mirrors ReviewStateAlert's
 * info-tone styling (border + tinted background + icon + title/body) so it
 * reads as part of the redesign's alert family, but it is not sticky — it
 * belongs with the header context, not the workflow state.
 *
 * A close (×) button in the top-right corner lets the reviewer collapse it into a
 * compact "View instructions" trigger (rendered by the parent, next to the
 * other header actions) once they've read it — the collapse/expand animation
 * and persisted choice live with the parent (ReviewDetail), this component
 * only surfaces the trigger.
 */
export interface InstructionsCalloutProps {
  /** Normalised instruction text (see reviewLogic.applicationInstructionText). */
  text: string;
  /** Collapses the block into the compact header-action trigger. */
  onCollapse: () => void;
}

const InstructionsCallout = ({ text, onCollapse }: InstructionsCalloutProps) => (
  <Box
    position="relative"
    border="1px solid"
    borderColor="p.200"
    borderRadius="component.4xl"
    // p.50 — the lightest azurite. `bg.azurite` resolves to p.100, which is a
    // step too strong for a full-width block of body copy.
    bg="p.50"
    p="lg"
    width="full"
    data-testid="instructions-callout"
  >
    <Box position="absolute" top="sm" right="sm">
      <Button
        variant="subtle"
        tone="neutral"
        size="xs"
        icon={<Icon as={FiX} boxSize={4} />}
        aria-label="Collapse instructions"
        onClick={onCollapse}
      />
    </Box>
    <Flex gap="md" align="flex-start" pr="2xl">
      <Icon as={BookOpen} boxSize={5} color="p.500" mt="1px" flexShrink={0} />
      <VStack align="flex-start" spacing="2px" minW={0}>
        <Text fontSize="smd" fontWeight={600} color="p.700">
          Instructions
        </Text>
        {/* Admin text may contain intentional line breaks — preserve them. */}
        <Text fontSize="smd" color="p.600" lineHeight="1.5" whiteSpace="pre-wrap">
          {text}
        </Text>
      </VStack>
    </Flex>
  </Box>
);

export default InstructionsCallout;
